import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  List, 
  ListItemButton, 
  ListItemText, 
  Collapse, 
  ListItemIcon 
} from '@mui/material';
import TranslateIcon from '@mui/icons-material/Translate';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';

interface Language {
  code: string;
  name: string;
}

const languages: Language[] = [
  { code: 'en', name: 'English' },
  { code: 'es', name: 'Español' }
];

interface LanguageSwitcherProps {
  closeParentMenu?: () => void;
}

export default function LanguageSwitcher({ closeParentMenu }: LanguageSwitcherProps) {
  const { i18n, t } = useTranslation();
  const [isOpen, setIsOpen] = useState<boolean>(false);
  
  const handleToggle = () => {
    setIsOpen(!isOpen);
  };
  
  const changeLanguage = (code: string) => {
    i18n.changeLanguage(code);
    setIsOpen(false);
    // If this component is used within another menu (like in the Navbar),
    // we can optionally close that menu too
    if (closeParentMenu) {
      closeParentMenu();
    }
  };
  
  return (
    <>
      <ListItemButton 
        onClick={handleToggle}
        sx={{ 
          minHeight: '48px',
          px: 2,
          display: 'flex',
          alignItems: 'center'
        }}
      >
        <ListItemIcon sx={{ minWidth: '36px' }}>
          <TranslateIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText 
          primary={t('navBar.navigationButtons.language')} 
          primaryTypographyProps={{ sx: { fontWeight: 500 } }}
        />
        {isOpen ? <ExpandLess /> : <ExpandMore />}
      </ListItemButton>
      <Collapse in={isOpen} timeout="auto" unmountOnExit>
        <List component="div" disablePadding>
          {languages.map((lang) => (
            <ListItemButton 
              key={lang.code}
              onClick={() => changeLanguage(lang.code)}
              selected={i18n.language === lang.code}
              sx={{ 
                pl: 4,
                minHeight: '40px' 
              }}
            >
              <ListItemText 
                primary={lang.name} 
                primaryTypographyProps={{ 
                  sx: { 
                    fontWeight: i18n.language === lang.code ? 500 : 400 
                  } 
                }}
              />
            </ListItemButton>
          ))}
        </List>
      </Collapse>
    </>
  );
}
